/**
 * TermsOfService — public-accessible legal document.
 *
 * Pairs with PrivacyPolicy. Same disclaimers apply: factually accurate
 * to current FrameFlow behaviour but should be reviewed by counsel
 * before any production launch and before changing pricing / refunds /
 * acceptable-use clauses.
 */

import React from 'react';
import LegalShell from '../components/LegalShell';

const TermsOfService = () => (
  <LegalShell
    eyebrow="Legal"
    title="Terms of Service"
    effectiveDate="May 19, 2026"
    lastUpdated="May 19, 2026"
  >
    <p>
      These Terms of Service (&ldquo;Terms&rdquo;) govern your use of FrameFlow,
      a film-production management platform operated by Cinerian
      (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;). By creating an
      account or otherwise using the service, you agree to these Terms. If
      you don&rsquo;t agree, please don&rsquo;t use the service.
    </p>

    <h2>1. The service</h2>
    <p>
      FrameFlow lets you organise films, scenes, shots, cast and crew across
      shared workspaces, parse scripts, manage subscriptions and collaborate
      with the team-mates you invite. Features available to you depend on
      the plan your workspace is on (Free, Pro, Studio, etc.).
    </p>

    <h2>2. Your account</h2>
    <p>
      You must be at least 16 years old to create an account. You&rsquo;re
      responsible for keeping your login credentials confidential and for any
      activity that happens under your account. Notify us at{' '}
      <a href="mailto:support@cinerian.com">support@cinerian.com</a>{' '}
      immediately if you suspect unauthorised use.
    </p>

    <h2>3. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Upload or share content you don&rsquo;t have the rights to use</li>
      <li>Use the service for anything unlawful, harassing, fraudulent or harmful</li>
      <li>Attempt to bypass authentication, rate limits, plan restrictions, or other technical controls</li>
      <li>Reverse-engineer, scrape, or otherwise probe the service for vulnerabilities outside of an authorised disclosure programme</li>
      <li>Resell, sublicense, or commercially redistribute the service to third parties without our written consent</li>
      <li>Send spam, malware, or any content that infringes another person&rsquo;s intellectual-property or privacy rights</li>
    </ul>
    <p>
      We may suspend or terminate accounts that violate these rules. Where
      practical, we will give you a chance to fix the issue first.
    </p>

    <h2>4. Your content</h2>
    <p>
      You retain all rights to the content you create or upload &mdash;
      scripts, scenes, shots, notes, cast and crew records, asset uploads, and
      anything else. You grant FrameFlow a limited, worldwide, royalty-free
      licence to host, store and display that content solely to provide the
      service to you and the collaborators you invite. The licence ends when
      you delete the content or your account.
    </p>
    <p>
      You confirm that you have all necessary rights, consents and
      authorisations for the content you upload. If a third party asserts a
      valid claim against the content, we may remove it.
    </p>

    <h2>5. Plans, billing and refunds</h2>
    <p>
      Paid plans are billed in advance through our payment processor,
      Razorpay, in the currency and on the cycle shown at checkout. Plan
      capabilities are described on the pricing pages and in your workspace
      settings.
    </p>
    <ul>
      <li>
        <strong>Renewal</strong>: subscriptions renew automatically at the
        end of each cycle unless cancelled.
      </li>
      <li>
        <strong>Cancellation</strong>: you may cancel at any time from your
        workspace settings. Your plan remains active until the end of the
        current paid period; no further charges will be made.
      </li>
      <li>
        <strong>Refunds</strong>: charges are non-refundable except where
        required by applicable law or where a charge is the result of a
        billing error caused by us. To request a refund, write to{' '}
        <a href="mailto:support@cinerian.com">support@cinerian.com</a>{' '}
        within 14 days of the charge.
      </li>
      <li>
        <strong>Downgrades and overage</strong>: if you downgrade to a plan
        with lower limits, content in excess of the new limits may be
        archived or restricted. We&rsquo;ll show you what will be affected
        before the downgrade takes effect.
      </li>
      <li>
        <strong>Taxes</strong>: prices may exclude applicable taxes (e.g.
        GST, VAT, sales tax), which will be added at checkout where required.
      </li>
    </ul>

    <h2>6. Beta features</h2>
    <p>
      Some parts of FrameFlow are labelled as &ldquo;beta&rdquo; or are
      otherwise marked as in active development. They&rsquo;re provided as
      they are; behaviour may change, break or disappear, and they are not
      covered by any uptime promise.
    </p>

    <h2>7. Availability and service changes</h2>
    <p>
      We&rsquo;ll try to keep the service available, but we don&rsquo;t
      guarantee any specific uptime unless you have a separate written
      service-level agreement with us. We may add, change or remove features
      from time to time; for material changes that impact a paid plan, we
      will give reasonable advance notice via email or in-product.
    </p>

    <h2>8. Termination</h2>
    <p>
      You can stop using the service and delete your account at any time. We
      may suspend or terminate access if you breach these Terms, if required
      by law, or if we discontinue the service. On termination, your right to
      access the service ends; we will keep or delete your data per our
      Privacy Policy and applicable retention rules.
    </p>

    <h2>9. Disclaimers</h2>
    <p>
      The service is provided <strong>as is</strong> and <strong>as
      available</strong>, without warranties of any kind, whether express or
      implied. We do not warrant that the service will be uninterrupted,
      error-free, or that data will never be lost. You are responsible for
      keeping your own backups of anything critical.
    </p>

    <h2>10. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, FrameFlow and Cinerian
      won&rsquo;t be liable for indirect, incidental, special, consequential
      or punitive damages, or for any loss of profit, revenue, data, business
      or goodwill, even if we&rsquo;ve been advised of the possibility. Our
      aggregate liability for any claim relating to the service is limited to
      the amount you paid us for the service in the 12 months preceding the
      event giving rise to the claim, or USD 100, whichever is greater.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to indemnify and hold FrameFlow harmless from claims, losses
      and expenses (including reasonable lawyers&rsquo; fees) arising from
      content you upload, your use of the service, or your breach of these
      Terms.
    </p>

    <h2>12. Governing law and disputes</h2>
    <p>
      These Terms are governed by the laws of the jurisdiction in which
      Cinerian is established, without regard to its conflict-of-laws rules.
      Disputes will be brought in the competent courts of that jurisdiction.
      Nothing here removes any mandatory consumer rights you have under your
      local law.
    </p>

    <h2>13. Changes to these terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be
      announced inside the product and via email (for paid customers) at
      least 14 days before they take effect. Continued use of the service
      after the effective date means you accept the updated Terms.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions about these Terms:{' '}
      <a href="mailto:reach@cinerian.com">reach@cinerian.com</a>
      <br />
      Support and account issues:{' '}
      <a href="mailto:support@cinerian.com">support@cinerian.com</a>
    </p>
  </LegalShell>
);

export default TermsOfService;
